import * as React from 'react';
import {
  Box,
  Button,
  InputStatusMessage,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { CollapsibleSection } from '@web-config-app/core-react-ui';
import { getVisibleHiddenUnicodeParts } from '@web-config-app/core';
import { useHiddenUnicodeWarning } from './use-hidden-unicode-warning.hook';

interface HiddenUnicodeWarningProps {
  id: string;
  currentValue: string;
  path: string;
  enabled: boolean;
  onChange: (path: string, nextValue: string | undefined) => void;
}

export const HiddenUnicodeWarning: React.FC<HiddenUnicodeWarningProps> = ({
  id,
  currentValue,
  path,
  enabled,
  onChange,
}) => {
  const warningMessageId = `${id}-hidden-unicode-warning`;
  const { formatMessage } = useIntl();
  const { hasHiddenCharacters, removeHiddenCharacters } =
    useHiddenUnicodeWarning({
      value: currentValue,
      path,
      enabled,
      onChange,
    });

  if (!hasHiddenCharacters) {
    return null;
  }

  const visibleValueParts = getVisibleHiddenUnicodeParts(currentValue);

  return (
    <StackLayout
      orientation="vertical"
      spacing="$quarter"
      css={{ marginBlockStart: '$quarter' }}
    >
      <InputStatusMessage
        inputStatus="warning"
        id={warningMessageId}
        statusIconLabel={formatMessage({ id: 'WARNING' })}
        css={{ color: '$onSurfaceTextWarning' }}
      >
        {formatMessage({
          id: 'HIDDEN_UNICODE_CHARACTERS_DETECTED',
        })}
      </InputStatusMessage>
      <CollapsibleSection
        label={formatMessage({
          id: 'VIEW_FULL_VALUE',
        })}
        expandedLabel={formatMessage({
          id: 'HIDE_FULL_VALUE',
        })}
        triggerPriority="secondary"
        trailingContent={
          <Button
            quiet
            priority="secondary"
            destructive
            size="small"
            disabled={!enabled}
            onClick={removeHiddenCharacters}
          >
            {formatMessage({
              id: 'REMOVE_HIDDEN_CHARACTERS',
            })}
          </Button>
        }
      >
        <ParagraphText size="sm">
          {visibleValueParts.map((part) =>
            part.isHiddenUnicode ? (
              <Box
                as="span"
                key={`${part.position}-${part.value}`}
                css={{
                  backgroundColor: '$warningBackgroundSubdued',
                  fontFamily: 'monospace',
                  opacity: 0.85,
                }}
              >
                {part.value}
              </Box>
            ) : (
              <React.Fragment key={`${part.position}-${part.value}`}>
                {part.value}
              </React.Fragment>
            ),
          )}
        </ParagraphText>
      </CollapsibleSection>
    </StackLayout>
  );
};
